// src/features/system/developer/components/video-gallery/format.ts
// Pure formatters used by the gallery components.
//
// Status vocabulary, stage ordering, terminal classification, and
// progress derivation live in `./states.ts` — single source of truth
// for the pipeline state machine on the FE.

import { stageLabel, isFailed, isInFlight, WARMING_UP_LABEL } from './states';
import type { VideoFeedItem } from './types';

// File-name capture timestamp pattern used by PAC capture clients.
// Example: 20260514_124754_0263dd42.mp4
const FILENAME_CAPTURE_TS_RX = /^(\d{4})(\d{2})(\d{2})_(\d{2})(\d{2})(\d{2})(?:_|\.)/;

// ── Numeric-field normalization (single source of truth) ────────────────────
//
// `VideoFeedItem` declares `created_at_ms`, `byte_length`, and `duration_ms`
// as `number`, but the gallery ingests items from heterogeneous sources —
// the BFF digest (always numeric), localStorage `lastGoodSnapshot` rehydration,
// optimistic inserts, and SSE/stream merges — where a field can arrive as a
// numeric string, `undefined`, or `NaN`. A single unparseable timestamp used
// to mis-bucket a row into "today" (silent fallback) and then get filtered
// out of every day window. To make that class of bug structurally impossible,
// EVERY item is funnelled through `normalizeFeedItem` at the hook's ingestion
// boundary, so all downstream consumers can rely on the numeric invariant
// without re-defending. Keep these helpers pure — they are unit-gated.

/** Coerce any value to a finite epoch-ms number. Non-finite → 0. */
export function toEpochMs(value: unknown): number {
  const ms = Number(value);
  return Number.isFinite(ms) ? ms : 0;
}

/** Coerce any value to a finite, strictly-positive number. Invalid → 0. */
export function toFiniteNonNegative(value: unknown): number {
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? n : 0;
}

/**
 * Normalize a feed item's numeric invariants (`created_at_ms`, `byte_length`,
 * `duration_ms`) regardless of source. Returns the SAME object reference when
 * every field is already a valid number, so referential-equality memoization
 * downstream (e.g. `React.memo(VideoCard)`) is preserved on the common path
 * and only malformed rows allocate.
 */
export function normalizeFeedItem(item: VideoFeedItem): VideoFeedItem {
  const created = toEpochMs(item.created_at_ms);
  const bytes   = toFiniteNonNegative(item.byte_length);
  const dur     = toFiniteNonNegative(item.duration_ms);
  if (
    created === item.created_at_ms &&
    bytes   === item.byte_length &&
    dur     === item.duration_ms
  ) {
    return item;
  }
  return { ...item, created_at_ms: created, byte_length: bytes, duration_ms: dur };
}

/**
 * Canonical "video creation" timestamp used for day grouping and calendar
 * filters in the gallery.
 *
 * Priority:
 *   1) Parse local capture time from filename prefix (YYYYMMDD_HHMMSS)
 *      so day buckets are stable and do not drift with UTC conversion.
 *   2) Fallback to backend `created_at_ms` when filename has no timestamp.
 */
export function referenceCreatedAtMs(item: Pick<VideoFeedItem, 'filename' | 'created_at_ms'>): number {
  const name = String(item.filename || '');
  const m = name.match(FILENAME_CAPTURE_TS_RX);
  if (m) {
    const y  = Number(m[1]);
    const mo = Number(m[2]);
    const d  = Number(m[3]);
    const hh = Number(m[4]);
    const mm = Number(m[5]);
    const ss = Number(m[6]);
    const t = new Date(y, mo - 1, d, hh, mm, ss, 0).getTime();
    if (Number.isFinite(t) && t > 0) return t;
  }
  return toEpochMs(item.created_at_ms);
}

export function formatBytes(bytes: number): string {
  if (!bytes || bytes < 0) return '—';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  let i = 0;
  let v = bytes;
  while (v >= 1024 && i < units.length - 1) { v /= 1024; i++; }
  return `${v.toFixed(v >= 10 || i === 0 ? 0 : 1)} ${units[i]}`;
}

export function formatDuration(ms: number): string {
  if (!ms || ms <= 0) return '—';
  const totalSec = Math.round(ms / 1000);
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;
  if (h > 0) return `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  return `${m}:${String(s).padStart(2, '0')}`;
}

export function formatTimeAgo(ms: number, now = Date.now()): string {
  if (!ms) return '—';
  const diff = Math.max(0, now - ms);
  const sec = Math.floor(diff / 1000);
  if (sec < 60)        return `${sec}s ago`;
  const min = Math.floor(sec / 60);
  if (min < 60)        return `${min}m ago`;
  const hr  = Math.floor(min / 60);
  if (hr  < 24)        return `${hr}h ago`;
  const day = Math.floor(hr / 24);
  if (day < 7)         return `${day}d ago`;
  return new Date(ms).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

const STATUS_TONE: Record<string, { text: string; bg: string; border: string; label: string }> = {
  initiated:        { text: 'text-amber-300',   bg: 'bg-amber-500/10',  border: 'border-amber-400/40',  label: stageLabel('initiated') },
  uploaded:         { text: 'text-sky-300',     bg: 'bg-sky-500/10',    border: 'border-sky-400/40',    label: stageLabel('uploaded') },
  ingested:         { text: 'text-blue-300',    bg: 'bg-blue-500/10',   border: 'border-blue-400/40',   label: stageLabel('ingested') },
  extracted:        { text: 'text-indigo-300',  bg: 'bg-indigo-500/10', border: 'border-indigo-400/40', label: stageLabel('extracted') },
  transcribed:      { text: 'text-violet-300',  bg: 'bg-violet-500/10', border: 'border-violet-400/40', label: stageLabel('transcribed') },
  sidecars_written: { text: 'text-emerald-300', bg: 'bg-emerald-500/10',border: 'border-emerald-400/40',label: stageLabel('sidecars_written') },
  failed:           { text: 'text-rose-300',    bg: 'bg-rose-500/10',   border: 'border-rose-400/40',   label: stageLabel('failed') },
  unknown:          { text: 'text-neutral-400', bg: 'bg-neutral-700/30',border: 'border-neutral-600/40',label: 'Unknown' },
};

export function statusTone(status: string) {
  // In-flight without a known status key falls back to amber (active) rather
  // than neutral grey, so the UI never shows "Unknown" for a row that the
  // server confirms is running through the pipeline.
  if (STATUS_TONE[status]) return STATUS_TONE[status];
  if (isInFlight(status))  return STATUS_TONE.initiated;
  if (isFailed(status))    return STATUS_TONE.failed;
  return STATUS_TONE.unknown;
}

// Cold-start "warming up" tone — amber, intentionally distinct from the
// steady sky `uploaded` tone so the post-dispatch wake window reads as a
// transient, healthy state. Pulse animation is applied at the call site
// (VideoCard) so this stays a pure descriptor.
const WARMING_TONE = {
  text: 'text-amber-300',
  bg: 'bg-amber-500/10',
  border: 'border-amber-400/40',
  label: WARMING_UP_LABEL,
} as const;

export function warmingTone() {
  return WARMING_TONE;
}
